import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { useAuth } from "./useAuth";

/**
 * Native counterpart to the web /auth/callback route.
 *
 * In the browser, SailPoint redirects the page itself. In the native shell
 * there's no page to redirect — login happens in a system browser sheet, and
 * SailPoint returns to the app through the custom URL scheme registered in
 * Info.plist. iOS hands that URL to us here.
 */
export function useNativeAuthCallback() {
  const { finishLogin, adoptSession } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    const handle = CapApp.addListener("appUrlOpen", async ({ url }) => {
      // Custom-scheme URLs aren't reliably parsed by `new URL()` across
      // platforms, so pull the query string off directly.
      const query = url.includes("?") ? url.slice(url.indexOf("?") + 1) : "";
      const params = new URLSearchParams(query);
      const handedOffSession = params.get("session");
      const code = params.get("code");
      const state = params.get("state");

      // Normal path: the web callback completed the exchange and handed us a
      // session id, because SailPoint won't redirect to a custom scheme.
      if (handedOffSession) {
        try {
          await Browser.close();
        } catch {
          // Sheet already dismissed.
        }
        try {
          await adoptSession(handedOffSession);
          navigate("/", { replace: true });
        } catch {
          // adoptSession records the reason; the login screen shows it.
        }
        return;
      }

      if (!code || !state) return;

      // Dismiss the login sheet first so the user isn't left staring at a
      // spent SailPoint page while the exchange runs behind it.
      try {
        await Browser.close();
      } catch {
        // Already dismissed — nothing to do.
      }

      try {
        await finishLogin(code, state);
        navigate("/", { replace: true });
      } catch {
        // Swallowing this is what made a failed native sign-in look like a
        // silent bounce back to the login screen. finishLogin puts the reason
        // in auth state; LoginPage surfaces it.
      }
    });

    return () => {
      handle.then((h) => h.remove());
    };
  }, [finishLogin, adoptSession, navigate]);
}
